import i18n from '../plugins/i18n';
import { Filter, HotelOffers } from 'types';
import router from 'router';

const hashCode = (str: string): number => {
  let hash = 0;
  const ref = str.toLowerCase();
  if (ref.length == 0) return hash;
  for (let i = 0; i < ref.length; i++) {
    let char = ref.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
};

export const mapHotelOffers = (
  groups: HotelOffers.Group[],
  filters: HotelOffers.Filter,
  guests: number,
): HotelOffers.Group[] => {
  const mappedGroups = groups.map(it => {
    const mapped = it.offers.map(a => {
      const tmp = { ...a };
      tmp.filterRef = mapFilterRef(a);
      return tmp;
    });

    const copy = { ...it };
    copy.offers = mapped;
    copy.images = composeImages(it);
    return copy;
  });

  const trimmed = mappedGroups
    // .pipe<HotelOffers.Group>((it) => trimByOptions(it))
    // .pipe<HotelOffers.Group>((it) => matrixFilter(it, guests))
    // .pipe<HotelOffers.Group>((it) => extraTrimByBed(it, guests))
    // .pipe<Hotel.Group>((it) => checkCheapestPublic(it, mappedGroups))
    .pipe<HotelOffers.Group>(it => enrichFlagsAndLocalize(it, filters))
    .filter(item => item.offers.length > 0);

  if (trimmed.length > 0) {
    // watch FW-118
    return trimmed;
  } else {
    return mappedGroups;
  }
};

export const checkCheapestPublic = (
  trimmed: HotelOffers.Group[],
  origin: HotelOffers.Group[],
): HotelOffers.Group[] => {
  const hasPublicOffer = (
    group: HotelOffers.Group,
  ): {
    provider: string;
    hasPublic: boolean;
  }[] => {
    const providers = group.offers.map(it => it.price.provider);

    const hasPublic = (
      offer: HotelOffers.Offer[],
      provider: string,
    ): boolean => {
      return offer.some(
        x =>
          x.price.provider.toLowerCase().includes(provider.toLowerCase()) &&
          x.price.price &&
          !x.muted,
      );
    };

    return providers.map(it => {
      return {
        provider: it,
        hasPublic: hasPublic(group.offers, it),
      };
    });
  };

  const checked = trimmed.map(it => {
    const copy = { ...it };

    const publics = hasPublicOffer(it);

    publics.forEach(x => {
      if (!x.hasPublic) {
        const originGroup = origin.find(z => z.name === it.name);

        if (originGroup) {
          const publicCheapest = originGroup.offers
            .filter(z => {
              return (
                z.price.provider
                  .toLowerCase()
                  .includes(x.provider.toLowerCase()) && z.price.price
              );
            })
            .min(it => it.price.effectivePrice);

          if (publicCheapest) {
            it.offers.push(publicCheapest);
          }
        }
      }
    });

    return copy;
  });

  return checked;
};

export const composeImages = (group: HotelOffers.Group): string[] => {
  let defaultImage: string = null;
  const images = []
    .concat(...group.offers.map(it => it.images))
    .distinct()
    .filter(it => {
      if (it.includes('default')) {
        defaultImage = it;
        return false;
      } else {
        return true;
      }
    });

  if (images.length === 0 && defaultImage) {
    images.push(defaultImage);
  }

  return images;
};

export const mapFilterRef = (offer: HotelOffers.Offer): number[] => {
  const references: number[] = offer.flags
    .filter(it => filterExclusionaryCondition(it))
    .map(it => hashCode(it));

  references.push(hashCode(offer.bed)); // bed name to id
  return references;
};

export const mapPublicPrices = (
  group: HotelOffers.Group,
): HotelOffers.PublicPrice[] => {
  if (group.offers.length === 0) {
    return [];
  }

  const publicPrices: HotelOffers.PublicPrice[] = [];

  group.offers
    .map(it => {
      const publicPrice = it.price.strikethrough;

      let provider = it.price.provider.toLowerCase();

      if (provider === HotelOffers.PublicPriceSource.BOOKING.toLowerCase()) {
        provider = HotelOffers.PublicPriceSource.BOOKING;
      } else if (
        provider === HotelOffers.PublicPriceSource.EXPEDIA.toLowerCase()
      ) {
        provider = HotelOffers.PublicPriceSource.EXPEDIA;
      } else {
        return null;
      }

      const url = group.publicPrice.find(
        it => it.name.toLowerCase() === provider.toLocaleLowerCase(),
      )?.url;

      return {
        price: publicPrice ?? null,
        name: provider,
        url,
      } as HotelOffers.PublicPrice;
    })
    .filter(it => it != null)
    .groupBy(it => it.name)
    .forEach(it => {
      const min = it.filter(x => x.price != null).min(x => x.price);
      if (min) {
        publicPrices.push(min);
        return;
      }

      if (it.length > 0) {
        publicPrices.push(it[0]);
      }
    });

  return publicPrices ?? [];
};

const filterExclusionaryCondition = (x: string): boolean => {
  return (
    x != HotelOffers.FilterKeys.NonRefundable &&
    x != HotelOffers.FilterKeys.PartiallyRefundable
  );
};

const transformIDs = (
  searchFilterId: string,
): HotelOffers.FilterKeys | string => {
  switch (searchFilterId) {
    case Filter.OptionType.FreeBreakfast:
      return HotelOffers.FilterKeys.BreakfastIncluded;
    case Filter.OptionType.FreeCancellation:
      return HotelOffers.FilterKeys.FreeCancellation;
    case Filter.OptionType.PayLater:
      return HotelOffers.FilterKeys.PayLater;
    case Filter.OptionType.PayNow:
      return HotelOffers.FilterKeys.PayNow;
    default:
      return '';
  }
};

export const mapOffersFilter = (
  groups: HotelOffers.Group[],
  rawFilters: HotelOffers.Filter,
) => {
  groups = groups.map(it => hideTrimmed(it, false));

  const queryFiltersString = router.currentRoute.value.query.filters as string;

  const queryFilters =
    queryFiltersString?.split(',').map(it => transformIDs(it)) ?? [];

  const rawOptions = rawFilters.options;

  const allFlags = ([] as string[])
    .concat(
      ...groups.map(it =>
        ([] as string[]).concat(...it.offers.map(x => x.flags)),
      ),
    )
    .filter(it => filterExclusionaryCondition(it))
    .distinct(it => it);

  const allBeds = ([] as string[])
    .concat(...groups.map(it => it.offers.map(x => x.bed)))
    .distinct(it => it);

  const allOffers = ([] as HotelOffers.Offer[]).concat(
    ...groups.map(it => it.offers),
  );

  const filterSections: HotelOffers.FilterSectionModel[] = [];

  if (allOffers.length > 0) {
    const options = new HotelOffers.FilterSectionModel(
      String(i18n.global.t('hotelpage.rooms.filters.options')),
      allFlags.map(it => {
        return new Filter.Option({
          id: hashCode(it),
          name: getLocalizedOptionById(it, rawOptions).capitalize(),
          selected: queryFilters.includes(it),
        });
      }),
    );

    filterSections.push(options);
  }

  if (allBeds.length > 0) {
    const bed = new HotelOffers.FilterSectionModel(
      String(i18n.global.t('hotelpage.rooms.filters.bed')),
      allBeds.map(it => {
        return new Filter.Option({
          id: hashCode(it),
          name: it.capitalize(),
        });
      }),
    );

    filterSections.push(bed);
  }

  return filterSections;
};

export const filterByOptions = (
  groups: HotelOffers.Group[],
): HotelOffers.Group[] => {
  const showDuplicates = showDuplicatesFunc();

  const optionsFilterCondition = (option: string): boolean => {
    return (
      (option &&
        option != HotelOffers.FilterKeys.EligibleForPoints &&
        // option != HotelOffers.FilterKeys.nonRef &&
        option != HotelOffers.FilterKeys.PartiallyRefundable &&
        option != HotelOffers.FilterKeys.FreeCancellation) ||
      !option
    );
  };

  return groups.map((group: HotelOffers.Group) => {
    const filtered: HotelOffers.Offer[] = [];

    group.offers.forEach(offer => {
      const duplicateIndex = filtered.findIndex(x => {
        const aOptions = offer.flags.filter(it => optionsFilterCondition(it));
        const bOptions = x.flags.filter(it => optionsFilterCondition(it));
        return (
          aOptions.length === bOptions.length &&
          aOptions.every(a => bOptions.findIndex(b => b === a) >= 0)
        );
      });

      if (duplicateIndex < 0) {
        filtered.push(offer);
      } else {
        const isCancellationBetter =
          (!filtered[duplicateIndex].flags.includes(
            HotelOffers.FilterKeys.FreeCancellation,
          ) &&
            !filtered[duplicateIndex].flags.includes(
              HotelOffers.FilterKeys.PartiallyRefundable,
            ) &&
            (offer.flags.includes(HotelOffers.FilterKeys.FreeCancellation) ||
              offer.flags.includes(
                HotelOffers.FilterKeys.PartiallyRefundable,
              ))) ||
          (!filtered[duplicateIndex].flags.includes(
            HotelOffers.FilterKeys.FreeCancellation,
          ) &&
            filtered[duplicateIndex].flags.includes(
              HotelOffers.FilterKeys.PartiallyRefundable,
            ) &&
            offer.flags.includes(HotelOffers.FilterKeys.FreeCancellation));

        const listedLoyalty =
          filtered[duplicateIndex].flags.findIndex(
            x => x === HotelOffers.FilterKeys.EligibleForPoints,
          ) >= 0;
        const interatedLoyalty =
          offer.flags.findIndex(
            x => x === HotelOffers.FilterKeys.EligibleForPoints,
          ) >= 0;
        const hasImmunity = listedLoyalty && !interatedLoyalty;

        const isPriceLowerOrSame =
          offer.price.effectivePrice <=
          filtered[duplicateIndex].price.effectivePrice;

        if (isCancellationBetter && isPriceLowerOrSame && !hasImmunity) {
          const tmp = { ...filtered[duplicateIndex] };
          filtered.splice(duplicateIndex, 1);

          if (showDuplicates) {
            tmp.muted = true;
            tmp.cancelledBy =
              'Not the best price for this option set ' +
              offer.price.effectivePrice;
            filtered.push(tmp);
          }

          filtered.push(offer);
        } else if (!listedLoyalty && interatedLoyalty) {
          filtered.push(offer);
        } else if (isCancellationBetter && !isPriceLowerOrSame) {
          filtered.push(offer);
        } else if (showDuplicates) {
          const copy = { ...offer };
          copy.muted = true;
          copy.cancelledBy =
            'Not the best price for this option set  ' +
            offer.price.effectivePrice;
          filtered.push(copy);
        }
      }
    });

    const copy = { ...group };
    copy.offers = filtered;
    return copy;
  });
};

interface OptionsTrimmer {
  groups: HotelOffers.Group[];
  groupingBy: string;
  trimReasoon: string;
}

export const trimByOptions = (groups: HotelOffers.Group[]) => {
  const showDuplicates = showDuplicatesFunc();

  const trimmed = groups.map(group => {
    const groupedByOptions = group.offers.groupBy(offer => {
      const cancellation = (offer: HotelOffers.Offer): string => {
        if (
          offer.flags.some(it => it === HotelOffers.FilterKeys.FreeCancellation)
        ) {
          return 'full';
        }
        // if (offer.flags.some((it) => it === HotelOffers.FilterKeys.)) {
        //   return 'partial';
        // }
        return 'non';
      };

      const payment = offer.flags
        .filter(
          it =>
            it === HotelOffers.FilterKeys.PayAtProperty ||
            it === HotelOffers.FilterKeys.PayLater ||
            it === HotelOffers.FilterKeys.PayNow,
        )
        .join(',');

      return (
        'breakfast-' +
        offer.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded) +
        ', payment-' +
        payment +
        ', bed-' +
        offer.bed +
        ', loyalty-' +
        offer.flags.some(
          it => it === HotelOffers.FilterKeys.EligibleForPoints,
        ) +
        ', cancellation-' +
        cancellation(offer)
      );
    });

    const chunk: HotelOffers.Offer[] = [];

    const add = (x: HotelOffers.Offer) => {
      const added = chunk.some(it => it.price._token === x.price._token);
      if (!added) {
        chunk.push(x);
      }
    };

    groupedByOptions.forEach((offers, key) => {
      offers
        .sort((a, b) => a.price.effectivePrice - b.price.effectivePrice)
        .forEach((it, i) => {
          if (i == 0) {
            add(it);
          } else if (showDuplicates) {
            const copy = { ...it };
            copy.muted = true;
            copy.cancelledBy = 'Not the best price for this option set';
            add(copy);
          }
        });
    });

    const copy = { ...group };
    copy.offers = chunk;
    return copy;
  });

  return trimmed;
};

interface MatrixCell {
  offer: HotelOffers.Offer;
  x: number;
  y: number;
}

export const bedPriorityByGuest = (guest: number, bedName: string): number => {
  const bedsJSON = require('./../types/models/beds.json');

  const bedsList = bedsJSON as HotelOffers.BedPriority[];
  const bed = bedsList.find(
    it => it.name.toLowerCase() === bedName.toLowerCase(),
  );

  if (!bed || guest < 1 || guest > 4) {
    return 999;
  }

  return bed.priority[guest - 1];
};

export const bedSortingFunc = (a: string, b: string, guest: number): number => {
  return bedPriorityByGuest(guest, a) - bedPriorityByGuest(guest, b);
};

export const cancellationPriority = (offer: HotelOffers.Offer): number => {
  const refundable =
    offer.policyCancellationType ===
    HotelOffers.PolicyCancellationType.FreeCancellation;

  const partialRefundable =
    offer.policyCancellationType ===
    HotelOffers.PolicyCancellationType.PartiallyRefundable;

  return refundable ? 1 : partialRefundable ? 2 : 3;
};

export const matrixFilter = (groups: HotelOffers.Group[], guests: number) => {
  const showDuplicates = showDuplicatesFunc();

  return groups.map(group => {
    const groupedOffers = group.offers.groupBy(offer =>
      offer.flags.sortBy(it => hashCode(it)).join(','),
    );

    const filteredOffers: HotelOffers.Offer[] = [];

    groupedOffers.forEach((offers, i) => {
      var beds: string[] = [];
      var prices: number[] = [];

      offers.forEach(it => {
        beds.push(it.bed); // todo fix to static name
        prices.push(it.price.effectivePrice);
      });

      beds = beds
        .slice()
        .distinct()
        .sort((a, b) => bedSortingFunc(a, b, guests));

      prices = prices
        .slice()
        .distinct()
        .sort((a, b) => a - b);

      var sсhema: MatrixCell[] = [];

      offers.forEach(offer => {
        const x = beds.findIndex(
          it => it.toLowerCase() === offer.bed.toLowerCase(),
        );
        const y = prices.findIndex(it => it === offer.price.effectivePrice);

        sсhema.push({
          offer,
          x,
          y,
        });
      });

      sсhema = sсhema.sort((a, b) => a.x - b.x || a.y - b.y);

      const crossedOutRows: number[] = [];
      const crossedOutCols: number[] = [];

      sсhema.forEach(it => {
        const crossedOutX = crossedOutRows.findIndex(x => x === it.x) >= 0;
        const crossedOutY = crossedOutCols.findIndex(y => y === it.y) >= 0;

        if (it.offer.muted && showDuplicates) {
          filteredOffers.push(it.offer);
          return;
        }

        if (!crossedOutX && !crossedOutY) {
          filteredOffers.push(it.offer);
          crossedOutRows.push(it.x);
          crossedOutCols.push(it.y);
        } else if (showDuplicates) {
          const tmp = it.offer;
          if (!tmp.muted) {
            tmp.muted = true;
            tmp.cancelledBy = 'Not the best bed for this price';
          }
          filteredOffers.push(tmp);
        }
      });
    });

    const copy = { ...group };

    copy.offers = filteredOffers.sortBy(it => it.price.effectivePrice);
    return copy;
  });
};

export const extraTrimByBed = (
  groups: HotelOffers.Group[],
  guests: number,
): HotelOffers.Group[] => {
  const showDuplicates = showDuplicatesFunc();

  const mapped: HotelOffers.Group[] = groups.map(group => {
    const clone = { ...group };
    const newOffersCollection: HotelOffers.Offer[] = [];

    group.offers.forEach(offer => {
      const tmp = { ...offer };
      const bedPriority = bedPriorityByGuest(guests, offer.bed);

      if (bedPriority > 100) {
        if (showDuplicates) {
          tmp.muted = true;
          tmp.cancelledBy = 'Not the best bed for guests';
          newOffersCollection.push(tmp);
        }
        return;
      }

      if (
        offer.flags.some(x => x === HotelOffers.FilterKeys.EligibleForPoints)
      ) {
        newOffersCollection.push(tmp);
        return;
      }

      const needTrim = group.offers.some(it => {
        const bedBetter =
          bedPriorityByGuest(guests, it.bed) <=
          bedPriorityByGuest(guests, offer.bed);

        const isCancellationBetter =
          cancellationPriority(it) <= cancellationPriority(offer);

        const samePayment =
          HotelOffers.paymentType(it) === HotelOffers.paymentType(offer);

        const priceBetter =
          it.price.effectivePrice < offer.price.effectivePrice;

        const breakfast =
          offer.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded) !=
          it.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded)
            ? offer.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded)
              ? it.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded) &&
                offer.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded)
              : it.flags.includes(HotelOffers.FilterKeys.BreakfastIncluded)
            : true;

        return (
          bedBetter &&
          isCancellationBetter &&
          samePayment &&
          breakfast &&
          priceBetter &&
          it.price._token != offer.price._token &&
          !it.muted &&
          !offer.muted
        );
      });

      if (needTrim) {
        if (showDuplicates) {
          tmp.muted = true;
          tmp.cancelledBy = 'Not the best bed for guests';
          newOffersCollection.push(tmp);
        }
      } else {
        newOffersCollection.push(tmp);
      }
    });

    clone.offers = newOffersCollection;
    return clone;
  });

  return mapped;
};

export const filterByBed = (
  groups: HotelOffers.Group[],
  guests: number,
): HotelOffers.Group[] => {
  const showDuplicates = showDuplicatesFunc();

  return groups.map(z => {
    const xs: HotelOffers.Offer[] = [];
    z.offers.forEach(it => {
      if (it.muted && showDuplicates) {
        xs.push(it);
        return;
      }

      const duplicateIndex = xs.findIndex(x => {
        const aOptions = it.flags.filter(
          it => it != HotelOffers.FilterKeys.EligibleForPoints,
        );
        const bOptions = x.flags.filter(
          it => it != HotelOffers.FilterKeys.EligibleForPoints,
        );

        return (
          aOptions.length === bOptions.length &&
          aOptions.every(a => bOptions.findIndex(b => b === a) >= 0)
        );
      });

      if (duplicateIndex < 0) {
        xs.push(it);
      } else {
        const listedBedPriority = bedPriorityByGuest(
          guests,
          xs[duplicateIndex].bed.toLowerCase(),
        );
        const iteratedBedPriority = bedPriorityByGuest(
          guests,
          it.bed.toLowerCase(),
        );
        const isBedBetter = iteratedBedPriority > listedBedPriority;
        const listedLoyalty =
          xs[duplicateIndex].flags.findIndex(
            x => x === HotelOffers.FilterKeys.EligibleForPoints,
          ) >= 0;
        const interatedLoyalty =
          it.flags.findIndex(
            x => x === HotelOffers.FilterKeys.EligibleForPoints,
          ) >= 0;
        const hasImmunity = listedLoyalty && !interatedLoyalty;

        if (isBedBetter && !hasImmunity) {
          const tmp = { ...xs[duplicateIndex] };
          xs.splice(duplicateIndex, 1);
          if (showDuplicates) {
            tmp.muted = true;
            tmp.cancelledBy = 'replaced, because have better bed type';
            xs.push(tmp);
          }
          xs.push(it);
        } else if (interatedLoyalty && !listedLoyalty) {
          xs.push(it);
        } else if (showDuplicates) {
          const copy = { ...it };
          copy.muted = true;
          copy.cancelledBy = 'deleted, because have better bed type';
          xs.push(copy);
        }
      }
    });
    const copy = { ...z };
    copy.offers = xs;
    return copy;
  });
};

export const hideTrimmed = (
  group: HotelOffers.Group,
  showDuplicates: boolean,
): HotelOffers.Group => {
  if (showDuplicates) {
    return group;
  }

  const copy = { ...group };
  copy.offers = group.offers.filter(it => !it.muted);
  return copy;
};

export const showDuplicatesFunc = (force: boolean = true): boolean => {
  if (force) {
    return force;
  }

  return HotelOffers.OffersTrim.isTrimming();
  // return testTool != null && testTool.hotelModule.showDuplicates;
};

const getLocalizedOptionById = (
  origin: string,
  rawOptions: HotelOffers.OptionsLocalized[],
): string => {
  const option = rawOptions.find(it => it.origin === origin);

  let localized = option?.localized || null;

  if (localized == null) {
    switch (origin) {
      case HotelOffers.FilterKeys.NonRefundable:
      case HotelOffers.PolicyCancellationType.NonRefundable:
        localized = i18n.global.t('filters.nonRefundable');
        break;

      case HotelOffers.FilterKeys.PartiallyRefundable:
      case HotelOffers.PolicyCancellationType.PartiallyRefundable:
        localized = i18n.global.t('filters.partiallyRefundable');
        break;

      default:
        break;
    }
  }

  return localized ? localized : origin;
};

export const enrichFlagsAndLocalize = (
  groups: HotelOffers.Group[],
  rawFilters: HotelOffers.Filter,
): HotelOffers.Group[] => {
  return groups.map(it => {
    const group = { ...it } as HotelOffers.Group;

    group.offers = group.offers.map(it => {
      const offer = { ...it };

      const flags: string[] = [];

      flags.push(...offer.flags);

      switch (offer.policyCancellationType) {
        case HotelOffers.PolicyCancellationType.FreeCancellation:
          flags.push(HotelOffers.FilterKeys.FreeCancellation);
          break;

        case HotelOffers.PolicyCancellationType.PartiallyRefundable:
          flags.push(HotelOffers.FilterKeys.PartiallyRefundable);
          break;

        case HotelOffers.PolicyCancellationType.NonRefundable:
          flags.push(HotelOffers.FilterKeys.NonRefundable);
          break;

        default:
          break;
      }

      offer.flags = flags.distinct().map(it => it);

      offer.localizedOptions = offer.flags.map(it => {
        return {
          origin: it,
          localized: getLocalizedOptionById(it, rawFilters.options),
        };
      });

      return offer;
    });

    return group;
  });
};
